/**
 * RegimeValidationPanel — QST-103 P4 (refactored under QST-214)
 *
 * Renders the most recent regime validation snapshot (written by
 * `python3 scripts/regime_validation.py --upload`).
 *
 * QST-214 refactor: the panel now consumes a `QueryState<Snapshot |
 * null>` prop instead of calling `useQuery` directly. This puts it
 * on the same pattern as StatsOverview/SignalTable/AttributionPanel
 * — every panel renders a `LoadingState` / `ErrorState` /
 * `EmptyState` from the shared primitives, and `App.tsx` is the
 * single place that wires Convex queries to panels.
 *
 * Why a single panel (not two)? Flags and cells are tightly
 * coupled — a flag's verdict is the AND of its constituent cell
 * verdicts. Rendering them side-by-side makes the relationship
 * obvious without requiring drill-down UI.
 *
 * No inline business logic: all verdicts come from the report.
 * This component formats what it reads.
 */

import type { QueryState } from "../hooks/useQueryWithState";
import { LoadingState, ErrorState, EmptyState } from "./states";

// ── Types matching the JSON contract ────────────────────────────
// These are intentionally structural (not imported from Convex
// _generated types) because the `report` field is stored as
// `v.any()` — its schema lives in the Python dataclasses.

interface CellResult {
  agent: string;
  direction: string;
  regime_field: string;
  regime_value: string;
  n_total_signals: number;
  n_decisive: number;
  n_wins: number;
  n_losses: number;
  wr_mean: number;
  wr_ci_low: number;
  wr_ci_high: number;
  p_edge: number;
  pnl_mean_usd: number;
  verdict:
    | "empty"
    | "insufficient_evidence"
    | "consistent_with_baseline"
    | "edge_confirmed"
    | "edge_against"
    | "mixed";
  verdict_reason: string;
}

interface FlagResult {
  id: string;
  description: string;
  location: string;
  current_value: unknown;
  unlockable: boolean;
  criteria_summary: string;
  pass_cells: string[];
  fail_cells: string[];
  supporting_cell_verdicts: Record<string, string>;
  blocking_cell_verdicts: Record<string, string>;
}

interface Report {
  generated_at: string;
  spec_version: string;
  corpus_total: number;
  corpus_agents: Record<string, number>;
  exit_code: number;
  cells: CellResult[];
  flags: FlagResult[];
}

export interface Snapshot {
  generated_at: string;
  generated_at_ms: number;
  spec_version: string;
  exit_code: number;
  corpus_total: number;
  n_cells: number;
  n_flags: number;
  n_flags_unlockable: number;
  uploaded_at: number;
  report: Report;
}

interface RegimeValidationPanelProps {
  /**
   * `null` is the "no snapshot uploaded yet" empty state, returned
   * by the underlying Convex query. The discriminated union still
   * narrows correctly: `state.data` may be `Snapshot | null`, and
   * we branch on that inside the success arm.
   */
  state: QueryState<Snapshot | null>;
}

// ── Verdict colouring ───────────────────────────────────────────
// Matches the traffic-light semantics from regime_cells.Verdict.
// Keep this as a small lookup, not inline switch, so the mapping
// is auditable from one place.
const VERDICT_STYLE: Record<string, { colour: string; label: string }> = {
  edge_confirmed: { colour: "var(--green)", label: "EDGE CONFIRMED" },
  edge_against: { colour: "var(--red)", label: "EDGE AGAINST" },
  consistent_with_baseline: {
    colour: "var(--yellow)",
    label: "BASELINE",
  },
  insufficient_evidence: {
    colour: "var(--text-dim)",
    label: "NEED MORE DATA",
  },
  mixed: { colour: "var(--yellow)", label: "MIXED" },
  empty: { colour: "var(--text-dim)", label: "EMPTY" },
};

function verdictChip(verdict: string) {
  const style = VERDICT_STYLE[verdict] ?? {
    colour: "var(--text-dim)",
    label: verdict.toUpperCase(),
  };
  return (
    <span
      style={{
        color: style.colour,
        fontFamily: "var(--font-mono)",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
      }}
    >
      ● {style.label}
    </span>
  );
}

function formatRelativeTime(ms: number): string {
  const delta = Date.now() - ms;
  const mins = Math.floor(delta / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function cellLabel(cell: CellResult): string {
  return `${cell.agent}.${cell.direction}.${cell.regime_field}=${cell.regime_value}`;
}

// ── Component ───────────────────────────────────────────────────

export function RegimeValidationPanel({ state }: RegimeValidationPanelProps) {
  if (state.status === "loading") {
    return (
      <section className="panel regime-panel">
        <h2>Regime Validation</h2>
        <LoadingState label="regime validation" block />
      </section>
    );
  }

  if (state.status === "error") {
    return (
      <section className="panel regime-panel">
        <h2>Regime Validation</h2>
        <ErrorState
          error={state.error}
          onRetry={state.retry}
          label="Regime validation"
          block
        />
      </section>
    );
  }

  const snapshot = state.data;

  // Empty state — no snapshot has ever been uploaded.
  if (snapshot === null) {
    return (
      <section className="panel regime-panel">
        <h2>Regime Validation</h2>
        <EmptyState
          message="No validation run yet. Run python3 scripts/regime_validation.py --upload to populate this panel."
          block
        />
      </section>
    );
  }

  const { report } = snapshot;
  // Sort cells by n_decisive desc so the cells with the most
  // evidence appear first — matches what a trader wants to see.
  const cellsSorted = [...report.cells].sort(
    (a, b) => b.n_decisive - a.n_decisive,
  );

  return (
    <section className="panel regime-panel">
      <h2>Regime Validation</h2>

      {/* Header strip */}
      <div
        style={{
          display: "flex",
          gap: "24px",
          flexWrap: "wrap",
          padding: "12px 16px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          marginBottom: "16px",
          fontSize: "0.8125rem",
        }}
      >
        <Stat label="Run" value={formatRelativeTime(snapshot.uploaded_at)} />
        <Stat
          label="Spec"
          value={snapshot.spec_version}
          mono
        />
        <Stat
          label="Exit"
          value={String(snapshot.exit_code)}
          colour={
            snapshot.exit_code === 0
              ? "var(--green)"
              : snapshot.exit_code === 2
              ? "var(--red)"
              : "var(--yellow)"
          }
        />
        <Stat label="Signals" value={String(snapshot.corpus_total)} />
        <Stat
          label="Flags unlockable"
          value={`${snapshot.n_flags_unlockable} / ${snapshot.n_flags}`}
          colour={
            snapshot.n_flags_unlockable === snapshot.n_flags
              ? "var(--green)"
              : snapshot.n_flags_unlockable > 0
              ? "var(--yellow)"
              : "var(--text)"
          }
        />
        <Stat label="Cells" value={String(snapshot.n_cells)} />
      </div>

      {/* Flags table */}
      <h3 style={{ margin: "16px 0 8px", fontSize: "0.875rem", fontWeight: 600 }}>
        Flags ({report.flags.length})
      </h3>
      {report.flags.length === 0 ? (
        <EmptyState message="No flags registered." />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Flag</th>
                <th>Status</th>
                <th>Unlock criteria</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {report.flags.map((flag) => (
                <tr key={flag.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{flag.id}</div>
                    <div
                      style={{
                        color: "var(--text-dim)",
                        fontSize: "0.75rem",
                        marginTop: "2px",
                      }}
                    >
                      {flag.description}
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        color: flag.unlockable
                          ? "var(--green)"
                          : "var(--text-dim)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {flag.unlockable ? "● UNLOCKABLE" : "○ LOCKED"}
                    </span>
                  </td>
                  <td style={{ fontSize: "0.8125rem" }}>
                    {flag.criteria_summary}
                  </td>
                  <td
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.75rem",
                      color: "var(--text-dim)",
                    }}
                  >
                    {flag.location}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cells table */}
      <h3 style={{ margin: "24px 0 8px", fontSize: "0.875rem", fontWeight: 600 }}>
        Cells ({cellsSorted.length})
      </h3>
      {cellsSorted.length === 0 ? (
        <EmptyState message="No cells to display." />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Cell</th>
                <th style={{ textAlign: "right" }}>n (decisive)</th>
                <th style={{ textAlign: "right" }}>W / L</th>
                <th style={{ textAlign: "right" }}>WR (95% CI)</th>
                <th style={{ textAlign: "right" }}>P(edge)</th>
                <th>Verdict</th>
              </tr>
            </thead>
            <tbody>
              {cellsSorted.map((cell, i) => (
                <tr key={`${cellLabel(cell)}-${i}`}>
                  <td
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {cellLabel(cell)}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
                    {cell.n_decisive}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
                    <span style={{ color: "var(--green)" }}>{cell.n_wins}</span>
                    {" / "}
                    <span style={{ color: "var(--red)" }}>{cell.n_losses}</span>
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {cell.n_decisive === 0
                      ? "—"
                      : `${(cell.wr_mean * 100).toFixed(1)}% [${(cell.wr_ci_low * 100).toFixed(0)}–${(cell.wr_ci_high * 100).toFixed(0)}]`}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {cell.n_decisive === 0
                      ? "—"
                      : `${(cell.p_edge * 100).toFixed(0)}%`}
                  </td>
                  <td title={cell.verdict_reason}>
                    {verdictChip(cell.verdict)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Small helper: stat pill in the header strip ─────────────────

function Stat({
  label,
  value,
  colour,
  mono,
}: {
  label: string;
  value: string;
  colour?: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          color: "var(--text-dim)",
          fontSize: "0.6875rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: "2px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: colour ?? "var(--text)",
          fontWeight: 600,
          fontFamily: mono ? "var(--font-mono)" : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}
